import { useMemo } from 'react';

/**
 * Generic memoization hook for any shallow-stable object.
 * Accepts any object and optionally specific dependency keys.
 * If no keys are provided, all keys from the object are used.
 */
export function useMemoizedObject<T extends Record<string, any>>(
  rawObject: T | null | undefined,
  keys?: (keyof T)[]
): T | null {
  // Return early for nullish input
  if (!rawObject || typeof rawObject !== 'object') return null;

  // Resolve keys to track
  const resolvedKeys = keys && keys.length > 0 ? keys : (Object.keys(rawObject) as (keyof T)[]);

  // Resolve values for dependency array (memoized to avoid repeated calc)
  const dependencies = useMemo(
    () => {
      return resolvedKeys.map((key) => rawObject[key]);
    },
    resolvedKeys.map((key) => rawObject[key])
  );

  return useMemo(() => {
    return { ...rawObject };
  }, dependencies);
}
